import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ShieldCheck, Search, FileText, Calendar, Hash, CheckCircle, XCircle, Lock, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const VerifyPage = () => {
  const [documentNumber, setDocumentNumber] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!documentNumber.trim()) return;

    setLoading(true);
    setSearched(true);
    try {
      const res = await fetch(`${API_URL}/api/verify/${documentNumber.trim()}`);
      const data = await res.json();
      setResult(data);
    } catch {
      setResult({ found: false, status: 'error', message: 'Failed to verify document' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <div className="border-b border-slate-200 bg-white">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-slate-800 hover:text-slate-600 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <span className="font-semibold text-lg">Formlio</span>
          </Link>
          <div className="flex items-center gap-2 text-indigo-600">
            <ShieldCheck className="w-5 h-5" />
            <span className="font-medium text-sm">Document Verification</span>
          </div>
        </div>
      </div>

      <div className="max-w-xl mx-auto px-6 py-16">
        {/* Title */}
        <div className="text-center mb-10">
          <div className="w-16 h-16 bg-indigo-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <ShieldCheck className="w-8 h-8 text-indigo-600" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2" data-testid="verify-page-title">
            Verify Document
          </h1>
          <p className="text-slate-600">
            Enter a document number to verify its authenticity and status.
          </p>
        </div>

        {/* Search form */}
        <form onSubmit={handleVerify} className="mb-8" data-testid="verify-form">
          <div className="flex gap-2">
            <Input
              value={documentNumber}
              onChange={(e) => setDocumentNumber(e.target.value)}
              placeholder="FL032026-000001"
              className="h-12 text-base font-mono"
              data-testid="verify-input"
            />
            <Button
              type="submit"
              disabled={loading || !documentNumber.trim()}
              className="h-12 px-6 bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="verify-submit-btn"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Search className="w-5 h-5" />
              )}
            </Button>
          </div>
        </form>

        {/* Result */}
        {searched && result && (
          <Card className={`border-2 ${result.found ? 'border-green-200' : 'border-red-200'}`} data-testid="verify-result">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-3">
                {result.found ? (
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  </div>
                ) : (
                  <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                    <XCircle className="w-6 h-6 text-red-600" />
                  </div>
                )}
                <div>
                  <CardTitle className={result.found ? 'text-green-800' : 'text-red-800'}>
                    {result.found ? 'Document Verified' : 'Document Not Found'}
                  </CardTitle>
                  {!result.found && (
                    <p className="text-sm text-red-600 mt-1">{result.message}</p>
                  )}
                </div>
              </div>
            </CardHeader>

            {result.found && (
              <CardContent className="pt-0">
                <div className="space-y-0">
                  {/* Two-column layout */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
                    {/* Left Column */}
                    <div className="space-y-4 py-3 border-b border-slate-100">
                      <div>
                        <span className="text-xs text-slate-500 flex items-center gap-1 mb-1">
                          <ShieldCheck className="w-3 h-3" /> Document Status
                        </span>
                        <Badge className={result.status === 'valid' ? 'bg-green-600' : 'bg-red-600'} data-testid="verify-status">
                          {result.status === 'valid' ? 'Valid' : 'Invalid'}
                        </Badge>
                      </div>
                    </div>
                    <div className="space-y-4 py-3 border-b border-slate-100">
                      <div>
                        <span className="text-xs text-slate-500 flex items-center gap-1 mb-1">
                          <Hash className="w-3 h-3" /> Document Number
                        </span>
                        <span className="text-sm font-mono font-bold text-slate-900 block" data-testid="verify-doc-number">
                          {result.document_number}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
                    <div className="py-3 border-b border-slate-100">
                      <span className="text-xs text-slate-500 flex items-center gap-1 mb-1">
                        <FileText className="w-3 h-3" /> Title
                      </span>
                      <span className="text-sm font-medium text-slate-900 block" data-testid="verify-form-name">
                        {result.form_name}
                      </span>
                    </div>
                    <div className="py-3 border-b border-slate-100">
                      <span className="text-xs text-slate-500 flex items-center gap-1 mb-1">
                        <Calendar className="w-3 h-3" /> Submission Date
                      </span>
                      <span className="text-sm text-slate-900 block" data-testid="verify-date">
                        {result.submission_date ? new Date(result.submission_date).toLocaleDateString('en-US', {
                          year: 'numeric', month: 'long', day: 'numeric'
                        }) : '-'}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
                    <div className="py-3 border-b border-slate-100">
                      <span className="text-xs text-slate-500 flex items-center gap-1 mb-1">
                        <CheckCircle className="w-3 h-3" /> Signature Status
                      </span>
                      <Badge variant="outline" className="text-green-700 border-green-300" data-testid="verify-sig-status">
                        {result.signature_status}
                      </Badge>
                    </div>
                    <div className="py-3 border-b border-slate-100">
                      <span className="text-xs text-slate-500 flex items-center gap-1 mb-1">
                        <ShieldCheck className="w-3 h-3" /> Integrity Check
                      </span>
                      <Badge
                        className={result.integrity_check === 'Not Modified' ? 'bg-green-600' : 'bg-red-600'}
                        data-testid="verify-integrity"
                      >
                        {result.integrity_check}
                      </Badge>
                    </div>
                  </div>

                  {/* Full width hash */}
                  <div className="py-3">
                    <span className="text-xs text-slate-500 flex items-center gap-1 mb-1">
                      <Lock className="w-3 h-3" /> Document Hash (SHA-256)
                    </span>
                    <span className="text-xs font-mono text-slate-700 break-all block bg-slate-50 rounded p-2" data-testid="verify-hash">
                      {result.document_hash}
                    </span>
                  </div>
                </div>

                {/* Footer notice */}
                <div className="mt-4 p-3 bg-slate-50 rounded-lg text-center">
                  <p className="text-xs text-slate-500">
                    This document was issued by <strong>Formlio</strong>. Verification confirms document existence and integrity only.
                  </p>
                </div>
              </CardContent>
            )}
          </Card>
        )}
      </div>
    </div>
  );
};

export default VerifyPage;
